/**
 * Modal form for creating and editing clauses.
 *
 * Uses the shared Modal component for dialog semantics, focus trap, and brand styling.
 * Features:
 * - All clause fields (name, section, order form, reference number, clauseNumber, etc.)
 * - Dynamic options array via ClauseOptionsEditor
 * - option.text (template-derived preview wording) is read-only display data,
 *   never sent in the create/update payload — the backend derives it on
 *   version activation and ignores any client-sent value
 * - isMultiSelect checkbox to allow multiple option selections
 * - Schedule trigger configuration (links clause option to schedule requirement)
 * - Clause ID editing with rename warning (sends new id to backend for atomic rename)
 * - Referencing-context banner: when editing a clause the active org
 *   template's tags reference (`referencingTags` prop non-empty), shows a
 *   slate info banner naming the referencing tags via ConflictTagList,
 *   before the ID field, so the author sees it before renaming. Absent for
 *   create mode and global templates (no `referencingTags` prop supplied).
 *   `referencedOptionIds` threads the same lookup through to
 *   ClauseOptionsEditor for per-option "In use" chips.
 * - Source-tag banner: when opened via UnusedTagsPanel's "Add Clause" beside
 *   a specific unknown template tag (`sourceTag` prop, create mode only),
 *   shows a slate info banner naming that tag as context. The ID field is
 *   never prefilled from it — a tag like clause_a_b_c cannot be split back
 *   into one clause ID, so the admin always types the intended ID.
 * - Guided rename migration CTA ("Migrate & rename…", `onMigrate` prop): shown
 *   beside the referencing-context banner (edit mode, clause is referenced)
 *   and beside the 409 lifecycle-conflict block (a ClauseInUseError, i.e.
 *   `conflictTags` is set). The parent (ClauseManager) owns the
 *   ClauseMigrationDialog itself — this form only fires the callback. On a
 *   successful migration the parent closes this form (it would otherwise be
 *   bound to the now-dead old clause id).
 * - Module clauses associate with EXACTLY ONE of an order form (free-text ID,
 *   legacy) or a module (dropdown sourced from the `modules` prop — the same
 *   template-schema modules the wizard uses) — a radio toggle picks which.
 *   Only the active association's field is sent; the inactive one is left
 *   undefined so the backend's merge-and-clear logic drops any stale value.
 * - Validation before submission
 * - Brand-compliant typography: .label class on <label> elements, text-body-sm for body text
 */

import { useState } from 'react';
import { Button, Modal } from '../common';
import { useCreateClause, useUpdateClause } from '../../hooks';
import { getClauseConflictTags } from '../../services/api';
import ClauseOptionsEditor from './ClauseOptionsEditor';
import ConflictTagList from './ConflictTagList';
import type { Clause, ClauseOption, ClauseSection, CreateClauseInput, UpdateClauseInput } from '../../types';
import type { OrgModule, OrgSchedule } from '../../types/orgTemplate';

type AssociationType = 'orderForm' | 'module';

interface ClauseFormProps {
  templateId: string;
  clause: Clause | null;
  onClose: () => void;
  initialId?: string;
  schedules?: OrgSchedule[];
  modules?: OrgModule[];
  /** Bare tag keys (from parseClauseTags) referencing this clause in the
   *  active org template. Non-empty in edit mode shows the referencing-
   *  context banner. Omitted for create mode and global templates. */
  referencingTags?: string[];
  /** Option ids the active org template references for this clause —
   *  threaded through to ClauseOptionsEditor for per-option "In use" chips. */
  referencedOptionIds?: string[];
  /** Bare tag key the admin clicked "Add Clause" beside in UnusedTagsPanel.
   *  Create mode only — shows an info banner naming the tag as context. The
   *  ID field is never prefilled from it (canonical-resolver-only policy). */
  sourceTag?: string;
  /** Opens the guided rename migration dialog (owned by the parent) for this
   *  clause, unfilled. Renders the "Migrate & rename…" CTA beside the
   *  referencing banner and beside a 409 conflict block; omitted (or in
   *  create mode) renders neither CTA. */
  onMigrate?: () => void;
}

interface FormData {
  id: string;
  name: string;
  section: ClauseSection;
  associationType: AssociationType;
  orderFormId: string;
  moduleId: string;
  referenceNumber: string;
  clauseNumber: string;
  instructionalText: string;
  options: ClauseOption[];
  isRequired: boolean;
  isMultiSelect: boolean;
  defaultOptionId: string;
  scheduleTriggerEnabled: boolean;
  scheduleTriggerScheduleId: string;
  scheduleTriggerOptionId: string;
  order: number;
}

let optionCounter = 0;
const emptyOption = (): ClauseOption => ({
  id: `opt-${++optionCounter}`,
  label: '',
  description: '',
});

export default function ClauseForm({
  templateId,
  clause,
  onClose,
  initialId,
  schedules,
  modules,
  referencingTags,
  referencedOptionIds,
  sourceTag,
  onMigrate,
}: ClauseFormProps) {
  const createClause = useCreateClause();
  const updateClause = useUpdateClause();
  const isEditing = !!clause;

  const [formData, setFormData] = useState<FormData>(() => {
    if (clause) {
      return {
        id: clause.id,
        name: clause.name,
        section: clause.section,
        associationType: clause.moduleId ? 'module' : 'orderForm',
        orderFormId: clause.orderFormId || '',
        moduleId: clause.moduleId || '',
        referenceNumber: clause.referenceNumber,
        clauseNumber: clause.clauseNumber || '',
        instructionalText: clause.instructionalText || '',
        options: clause.options,
        isRequired: clause.isRequired,
        isMultiSelect: clause.isMultiSelect || false,
        defaultOptionId: clause.defaultOptionId || '',
        scheduleTriggerEnabled: !!clause.scheduleTrigger,
        scheduleTriggerScheduleId: clause.scheduleTrigger?.scheduleId || '',
        scheduleTriggerOptionId: clause.scheduleTrigger?.triggerOptionId || '',
        order: clause.order,
      };
    }
    return {
      id: initialId || '',
      name: '',
      section: 'contract-details',
      associationType: 'orderForm',
      orderFormId: '',
      moduleId: '',
      referenceNumber: '',
      clauseNumber: '',
      instructionalText: '',
      options: [emptyOption(), emptyOption()],
      isRequired: false,
      isMultiSelect: false,
      defaultOptionId: '',
      scheduleTriggerEnabled: false,
      scheduleTriggerScheduleId: '',
      scheduleTriggerOptionId: '',
      order: 0,
    };
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  // Tags on the active template that block this mutation (409 lifecycle
  // guard) — rendered as copyable chips beside the submit error.
  const [conflictTags, setConflictTags] = useState<string[] | null>(null);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.id.trim()) newErrors.id = 'ID is required';
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.referenceNumber.trim()) newErrors.referenceNumber = 'Reference number is required';
    if (formData.section === 'module') {
      if (formData.associationType === 'orderForm' && !formData.orderFormId.trim()) {
        newErrors.orderFormId = 'Order form ID is required for module clauses';
      }
      if (formData.associationType === 'module' && !formData.moduleId.trim()) {
        newErrors.moduleId = 'A module is required for module clauses';
      }
    }
    if (formData.options.length < 2) newErrors.options = 'At least 2 options are required';
    if (formData.options.length > 4) newErrors.options = 'Maximum 4 options allowed';

    const emptyLabels = formData.options.filter((opt) => !opt.label.trim());
    if (emptyLabels.length > 0) newErrors.optionLabels = 'All options must have a label';

    const optionIds = formData.options.map((opt) => opt.id);
    if (new Set(optionIds).size !== optionIds.length) newErrors.optionIds = 'Option IDs must be unique';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setConflictTags(null);
    if (!validate()) return;

    const input: CreateClauseInput = {
      id: formData.id,
      name: formData.name,
      section: formData.section,
      orderFormId:
        formData.section === 'module' && formData.associationType === 'orderForm'
          ? formData.orderFormId
          : undefined,
      moduleId:
        formData.section === 'module' && formData.associationType === 'module'
          ? formData.moduleId
          : undefined,
      referenceNumber: formData.referenceNumber,
      clauseNumber: formData.clauseNumber || null,
      instructionalText: formData.instructionalText || null,
      options: formData.options.map((opt) => ({
        id: opt.id,
        label: opt.label,
        description: opt.description || undefined,
      })),
      isRequired: formData.isRequired,
      isMultiSelect: formData.isMultiSelect ? true : null,
      defaultOptionId: formData.defaultOptionId || null,
      scheduleTrigger: formData.scheduleTriggerEnabled
        ? { scheduleId: formData.scheduleTriggerScheduleId, triggerOptionId: formData.scheduleTriggerOptionId }
        : null,
      order: formData.order,
    };

    try {
      if (isEditing) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: _stripId, ...fieldsWithoutId } = input;
        const updateInput: UpdateClauseInput = { ...fieldsWithoutId };
        if (formData.id !== clause.id) {
          updateInput.id = formData.id;
        }
        await updateClause.mutateAsync({ templateId, clauseId: clause.id, input: updateInput });
      } else {
        await createClause.mutateAsync({ templateId, input });
      }
      onClose();
    } catch (err) {
      // A 409 from the active-template lifecycle guard: the message states the
      // migration sequence; the exact blocking tags render as copyable chips.
      // getClauseConflictTags only matches ApiError (an Error), so a conflict
      // always has err.message — no separate conflict fallback arm.
      setConflictTags(getClauseConflictTags(err));
      setErrors({
        submit: err instanceof Error ? err.message : 'Failed to save clause',
      });
    }
  };

  const handleChange = (field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const isPending = createClause.isPending || updateClause.isPending;

  return (
    <Modal isOpen={true} onClose={onClose} title={isEditing ? 'Edit Clause' : 'Create Clause'} maxWidth="sm:max-w-2xl">
      <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto space-y-4">
          {/* Source-tag banner — shown before the ID field so the admin sees
              the tag they're creating a clause for. Create mode only; the ID
              is never prefilled from the tag. */}
          {!isEditing && sourceTag && (
            <div className="p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
              <p className="text-body-sm text-slate-700 dark:text-slate-300">
                Creating a clause for template tag{' '}
                <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">{`{${sourceTag}}`}</code>{' '}
                — enter the intended ID below; it is not parsed from the tag.
              </p>
            </div>
          )}

          {/* Referencing-context banner — shown before the ID field so the
              author sees it before renaming. Edit mode only. */}
          {isEditing && referencingTags && referencingTags.length > 0 && (
            <div className="p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
              <p className="text-body-sm font-medium text-slate-700 dark:text-slate-300">
                Referenced by the active template
              </p>
              <p className="mt-1 text-body-sm text-slate-600 dark:text-slate-400">
                Renaming this clause or removing an option will orphan these tags in the Word
                template.
              </p>
              <div className="mt-1 text-body-sm text-slate-600 dark:text-slate-400">
                <ConflictTagList tags={referencingTags} />
              </div>
              {onMigrate && (
                <div className="mt-2">
                  <Button type="button" variant="secondary" size="sm" onClick={onMigrate}>
                    Migrate & rename…
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ID */}
          <div>
            <label htmlFor="clause-id" className="label">
              ID <span className="text-red-500">*</span>
            </label>
            <input
              id="clause-id"
              type="text"
              value={formData.id}
              onChange={(e) => handleChange('id', e.target.value)}
              placeholder="e.g., training, liquidated-damages"
              className="input mt-1"
            />
            {errors.id && <p className="mt-1 text-body-sm text-red-600 dark:text-red-400">{errors.id}</p>}
            {isEditing && formData.id !== clause.id && (
              <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-body-sm text-amber-700 dark:text-amber-400">
                  Changing the clause ID will require updating the matching tags in the Word template.
                </p>
              </div>
            )}
          </div>

          {/* Name */}
          <div>
            <label htmlFor="clause-name" className="label">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="clause-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Training (Clause 7.10)"
              className="input mt-1"
            />
            {errors.name && <p className="mt-1 text-body-sm text-red-600 dark:text-red-400">{errors.name}</p>}
          </div>

          {/* Section and Order Form */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="clause-section" className="label">
                Section <span className="text-red-500">*</span>
              </label>
              <select
                id="clause-section"
                value={formData.section}
                onChange={(e) => handleChange('section', e.target.value as ClauseSection)}
                className="input mt-1"
              >
                <option value="contract-details">Contract Details</option>
                <option value="module">Module</option>
              </select>
            </div>
            {formData.section === 'module' && (
              <div>
                <span className="label">Association</span>
                <div className="flex gap-4 mt-1 mb-2">
                  <label className="flex items-center gap-1.5 text-body-sm text-slate-700 dark:text-slate-300">
                    <input
                      type="radio"
                      name="clause-association-type"
                      value="orderForm"
                      checked={formData.associationType === 'orderForm'}
                      onChange={() => handleChange('associationType', 'orderForm')}
                      className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-slate-300 dark:border-slate-600"
                    />
                    Order Form
                  </label>
                  <label className="flex items-center gap-1.5 text-body-sm text-slate-700 dark:text-slate-300">
                    <input
                      type="radio"
                      name="clause-association-type"
                      value="module"
                      checked={formData.associationType === 'module'}
                      onChange={() => handleChange('associationType', 'module')}
                      className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-slate-300 dark:border-slate-600"
                    />
                    Module
                  </label>
                </div>
                {formData.associationType === 'orderForm' ? (
                  <>
                    <label htmlFor="clause-order-form" className="label">
                      Order Form ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="clause-order-form"
                      type="text"
                      value={formData.orderFormId}
                      onChange={(e) => handleChange('orderFormId', e.target.value)}
                      placeholder="e.g., 1a, 2b"
                      className="input mt-1"
                    />
                    {errors.orderFormId && <p className="mt-1 text-body-sm text-red-600 dark:text-red-400">{errors.orderFormId}</p>}
                  </>
                ) : (
                  <>
                    <label htmlFor="clause-module" className="label">
                      Associated Module <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="clause-module"
                      value={formData.moduleId}
                      onChange={(e) => handleChange('moduleId', e.target.value)}
                      className="input mt-1"
                    >
                      <option value="">Select module...</option>
                      {(modules ?? []).map((mod) => (
                        <option key={mod.id} value={mod.id}>{mod.name}</option>
                      ))}
                    </select>
                    {errors.moduleId && <p className="mt-1 text-body-sm text-red-600 dark:text-red-400">{errors.moduleId}</p>}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Reference Number, Clause Number, and Order */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="clause-ref" className="label">
                Reference Number <span className="text-red-500">*</span>
              </label>
              <input
                id="clause-ref"
                type="text"
                value={formData.referenceNumber}
                onChange={(e) => handleChange('referenceNumber', e.target.value)}
                placeholder="e.g., 19"
                className="input mt-1"
              />
              {errors.referenceNumber && <p className="mt-1 text-body-sm text-red-600 dark:text-red-400">{errors.referenceNumber}</p>}
            </div>
            <div>
              <label htmlFor="clause-number" className="label">
                Clause Number
              </label>
              <input
                id="clause-number"
                type="text"
                value={formData.clauseNumber}
                onChange={(e) => handleChange('clauseNumber', e.target.value)}
                placeholder="e.g., 7.1"
                className="input mt-1"
              />
              <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
                PDF bookmark. Uses Reference Number if blank.
              </p>
            </div>
            <div>
              <label htmlFor="clause-order" className="label">
                Display Order
              </label>
              <input
                id="clause-order"
                type="number"
                min="0"
                value={formData.order}
                onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
                className="input mt-1"
              />
            </div>
          </div>

          {/* Instructional Text */}
          <div>
            <label htmlFor="clause-instructions" className="label">
              Instructional Text
            </label>
            <textarea
              id="clause-instructions"
              value={formData.instructionalText}
              onChange={(e) => handleChange('instructionalText', e.target.value)}
              rows={2}
              placeholder="Instructions shown to users when selecting an option"
              className="textarea mt-1"
            />
          </div>

          {/* Required and Multi-select checkboxes */}
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center">
              <input
                id="clause-required"
                type="checkbox"
                checked={formData.isRequired}
                onChange={(e) => handleChange('isRequired', e.target.checked)}
                className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-slate-300 dark:border-slate-600 rounded"
              />
              <label htmlFor="clause-required" className="ml-2 text-body-sm text-slate-700 dark:text-slate-300">
                Selection required
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="clause-multiselect"
                type="checkbox"
                checked={formData.isMultiSelect}
                onChange={(e) => handleChange('isMultiSelect', e.target.checked)}
                className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-slate-300 dark:border-slate-600 rounded"
              />
              <label htmlFor="clause-multiselect" className="ml-2 text-body-sm text-slate-700 dark:text-slate-300">
                Allow multiple selections
              </label>
            </div>
          </div>

          {/* Schedule Trigger */}
          {schedules && schedules.length > 0 && (
            <div className="space-y-3 border dark:border-slate-600 rounded-lg p-3 bg-slate-50 dark:bg-slate-700">
              <div className="flex items-center">
                <input
                  id="clause-trigger-enabled"
                  type="checkbox"
                  checked={formData.scheduleTriggerEnabled}
                  onChange={(e) => handleChange('scheduleTriggerEnabled', e.target.checked)}
                  className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-slate-300 dark:border-slate-600 rounded"
                />
                <label htmlFor="clause-trigger-enabled" className="ml-2 text-body-sm text-slate-700 dark:text-slate-300">
                  Triggers a schedule requirement
                </label>
              </div>
              {formData.scheduleTriggerEnabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="trigger-option" className="label">
                      Trigger Option
                    </label>
                    <select
                      id="trigger-option"
                      value={formData.scheduleTriggerOptionId}
                      onChange={(e) => handleChange('scheduleTriggerOptionId', e.target.value)}
                      className="input mt-1"
                    >
                      <option value="">Select option...</option>
                      {formData.options.map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.label || opt.id}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="trigger-schedule" className="label">
                      Required Schedule
                    </label>
                    <select
                      id="trigger-schedule"
                      value={formData.scheduleTriggerScheduleId}
                      onChange={(e) => handleChange('scheduleTriggerScheduleId', e.target.value)}
                      className="input mt-1"
                    >
                      <option value="">Select schedule...</option>
                      {schedules.map((sch) => (
                        <option key={sch.id} value={sch.id}>{sch.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Options Editor */}
          <ClauseOptionsEditor
            clauseId={formData.id}
            options={formData.options}
            onChange={(options) => setFormData((prev) => ({ ...prev, options }))}
            defaultOptionId={formData.defaultOptionId}
            onDefaultChange={(id) => handleChange('defaultOptionId', id)}
            errors={errors}
            referencedOptionIds={referencedOptionIds}
          />

          {/* Submit error */}
          {errors.submit && (
            <div
              role="alert"
              className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
            >
              <p className="text-body-sm text-red-600 dark:text-red-400">{errors.submit}</p>
              {conflictTags && (
                <div className="text-body-sm text-red-600 dark:text-red-400">
                  <ConflictTagList tags={conflictTags} />
                </div>
              )}
              {isEditing && conflictTags && onMigrate && (
                <div className="mt-2">
                  <Button type="button" variant="secondary" size="sm" onClick={onMigrate}>
                    Migrate & rename…
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Form actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={isPending}>
              {isEditing ? 'Update Clause' : 'Create Clause'}
            </Button>
          </div>
      </form>
    </Modal>
  );
}
